// ============================================
// FOLDER MANAGER COMPONENT
// ============================================

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { 
  FolderPlus, 
  X, 
  Loader2, 
  MoreVertical, 
  Edit2, 
  Trash2,
  Folder
} from 'lucide-react';
import { useDocumentFolders, useCreateFolder, useUpdateFolder, useDeleteFolder } from '@/hooks';
import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

interface FolderManagerProps {
  selectedFolderId?: string | null;
  onFolderSelect?: (folderId: string | null) => void;
}

// Renk key'leri — çeviri panel:folders.colors.* ile yapılıyor
const FOLDER_COLORS = [
  { value: '#3B82F6', key: 'blue' },
  { value: '#10B981', key: 'green' },
  { value: '#F59E0B', key: 'orange' },
  { value: '#EF4444', key: 'red' },
  { value: '#8B5CF6', key: 'purple' },
  { value: '#EC4899', key: 'pink' },
  { value: '#6B7280', key: 'gray' },
];

export function FolderManager({ selectedFolderId, onFolderSelect }: FolderManagerProps) {
  const { t } = useTranslation(['panel', 'common']);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState<any>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#3B82F6');
  const [error, setError] = useState('');

  const { data: foldersResponse, isLoading } = useDocumentFolders();
  const createFolderMutation = useCreateFolder();
  const updateFolderMutation = useUpdateFolder();
  const deleteFolderMutation = useDeleteFolder();

  const folders: any[] = (foldersResponse as any)?.data || foldersResponse || [];

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newFolderName.trim()) {
      setError(t('panel:folders.folderNameRequired'));
      return;
    }

    try {
      await createFolderMutation.mutateAsync({
        name: newFolderName.trim(),
        color: newFolderColor,
      });
      setShowCreateModal(false);
      setNewFolderName('');
      setNewFolderColor('#3B82F6');
      setError('');
    } catch (err: any) {
      setError(err?.message || t('panel:folders.createFailed'));
    }
  };

  const handleUpdateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newFolderName.trim()) {
      setError(t('panel:folders.folderNameRequired'));
      return;
    }

    try {
      await updateFolderMutation.mutateAsync({
        id: editingFolder.id,
        data: {
          name: newFolderName.trim(),
          color: newFolderColor,
        },
      });
      setEditingFolder(null);
      setNewFolderName('');
      setNewFolderColor('#3B82F6');
      setError('');
    } catch (err: any) {
      setError(err?.message || t('panel:folders.updateFailed'));
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!confirm(t('panel:folders.deleteConfirm'))) {
      return;
    }

    try {
      await deleteFolderMutation.mutateAsync(folderId);
      if (selectedFolderId === folderId) {
        onFolderSelect?.(null);
      }
    } catch (err: any) {
      alert(err?.message || t('panel:folders.deleteFailed'));
    }
  };

  const openEditModal = (folder: any) => {
    setEditingFolder(folder);
    setNewFolderName(folder.name);
    setNewFolderColor(folder.color || '#3B82F6');
    setError('');
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">{t('panel:folders.title')}</h3>
        <button
          onClick={() => {
            setShowCreateModal(true);
            setNewFolderName('');
            setNewFolderColor('#3B82F6');
            setError('');
          }}
          className="p-1 text-gray-400 hover:text-maritime-600 hover:bg-maritime-50 rounded-maritime"
          title={t('panel:folders.newFolder')}
        >
          <FolderPlus className="w-4 h-4" />
        </button>
      </div>

      {/* Tüm Belgeler */}
      <button
        onClick={() => onFolderSelect?.(null)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 rounded-maritime text-sm transition-colors',
          selectedFolderId === null
            ? 'bg-maritime-100 text-maritime-700'
            : 'text-gray-600 hover:bg-gray-100'
        )}
      >
        <Folder className="w-4 h-4" />
        <span>{t('panel:folders.allDocuments')}</span>
      </button>

      {/* Klasör Listesi */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        </div>
      ) : (
        folders.map((folder) => (
          <div
            key={folder.id}
            className={cn(
              'group flex items-center gap-2 px-3 py-2 rounded-maritime text-sm transition-colors',
              selectedFolderId === folder.id
                ? 'bg-maritime-100 text-maritime-700'
                : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            <button
              onClick={() => onFolderSelect?.(folder.id)}
              className="flex items-center gap-2 flex-1 min-w-0"
            >
              <Folder 
                className="w-4 h-4 flex-shrink-0" 
                style={{ color: folder.color || '#6B7280' }} 
              />
              <span className="truncate">{folder.name}</span>
              {folder.documentCount > 0 && (
                <span className="text-xs text-gray-400">({folder.documentCount})</span>
              )}
            </button>

            {/* Klasör Aksiyonları */}
            <Popover.Root>
              <Popover.Trigger asChild>
                <button
                  className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  className="bg-white rounded-maritime shadow-lg border border-gray-200 py-1 z-50 min-w-[120px]"
                  sideOffset={5}
                >
                  <button
                    onClick={() => openEditModal(folder)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Edit2 className="w-4 h-4" />
                    {t('common:edit')}
                  </button>
                  <button
                    onClick={() => handleDeleteFolder(folder.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('common:delete')}
                  </button>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          </div>
        ))
      )}

      {/* Klasör Oluştur Modal */}
      <Dialog.Root open={showCreateModal} onOpenChange={setShowCreateModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-maritime shadow-lg w-full max-w-sm z-50">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {t('panel:folders.newFolder')}
              </Dialog.Title>
              <Dialog.Close className="p-1 rounded-maritime hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </Dialog.Close>
            </div>

            <form onSubmit={handleCreateFolder} className="p-4 space-y-4">
              {error && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-maritime text-sm text-red-600">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('panel:folders.folderName')} *
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="input w-full"
                  placeholder={t('panel:folders.folderNamePlaceholder')}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('panel:folders.color')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  {FOLDER_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setNewFolderColor(color.value)}
                      className={cn(
                        'w-8 h-8 rounded-full border-2 transition-all',
                        newFolderColor === color.value 
                          ? 'border-gray-900 scale-110' 
                          : 'border-transparent hover:scale-105'
                      )}
                      style={{ backgroundColor: color.value }}
                      title={t(`panel:folders.colors.${color.key}`)}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createFolderMutation.isPending}
                >
                  {createFolderMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('common:create')
                  )}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Klasör Düzenle Modal */}
      <Dialog.Root open={!!editingFolder} onOpenChange={(open) => !open && setEditingFolder(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-maritime shadow-lg w-full max-w-sm z-50">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {t('panel:folders.editFolder')}
              </Dialog.Title>
              <Dialog.Close className="p-1 rounded-maritime hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </Dialog.Close>
            </div>

            <form onSubmit={handleUpdateFolder} className="p-4 space-y-4">
              {error && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-maritime text-sm text-red-600">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('panel:folders.folderName')} *
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="input w-full"
                  placeholder={t('panel:folders.folderNamePlaceholder')}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('panel:folders.color')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  {FOLDER_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setNewFolderColor(color.value)}
                      className={cn(
                        'w-8 h-8 rounded-full border-2 transition-all',
                        newFolderColor === color.value 
                          ? 'border-gray-900 scale-110' 
                          : 'border-transparent hover:scale-105'
                      )}
                      style={{ backgroundColor: color.value }}
                      title={t(`panel:folders.colors.${color.key}`)}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditingFolder(null)}
                  className="btn-secondary"
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updateFolderMutation.isPending}
                >
                  {updateFolderMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('common:save')
                  )}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
